import { useEffect, useRef, useState } from "preact/hooks";
import { createIsland } from "@/utils/islands";
import { fetchApiGraphQL } from "@/utils/fetcher";
import { hideSpinner, showSpinner } from "@sitecorecomponents/BasicContent/Modal";
import { emailRules } from "@sitecorecomponents/Users/formValidation";
import { registerPropertyInterestMutation, RegisterPropertyInterestResult } from "./estate-property-interest.query";

type EstatePropertyInterestProps = {
    propertyNumber?: string;
    emailLabel: string;
    emailRequired: string;
    emailInvalid: string;
    submitButtonText: string;
    successText: string;
    alreadyRegisteredText: string;
    errorText: string;
};

export default function EstatePropertyInterestFormIsland(props: EstatePropertyInterestProps) {
    const [message, setMessage] = useState(props.propertyNumber ? null : props.errorText);
    const [email, setEmail] = useState("");
    const [validationKey, setValidationKey] = useState<string | null>(null);
    const messageRef = useRef<HTMLParagraphElement>(null);

    useEffect(() => {
        if (!message) {
            return;
        }
        messageRef.current?.closest("dialog")?.querySelectorAll<HTMLElement>(".component.text, .component.content").forEach((el) => {
            el.hidden = true;
        });
    }, [message]);

    const validate = () => {
        const failed = emailRules.find((rule) => !rule.test(email));
        setValidationKey(failed?.key ?? null);
        return !failed;
    };

    const submit = async (e: Event) => {
        e.preventDefault();
        if (!validate()) {
            return;
        }

        showSpinner();
        try {
            const result = await fetchApiGraphQL<RegisterPropertyInterestResult>(
                registerPropertyInterestMutation,
                { propertyNumber: props.propertyNumber, email: email.trim() },
                true,
            );
            const alreadyRegistered = result.data.registerPropertyInterest?.result === "AlreadyRegistered";
            setMessage(alreadyRegistered ? props.alreadyRegisteredText : props.successText);
        } catch (err) {
            console.log("Error while registering property interest", err);
            setMessage(props.errorText);
        } finally {
            hideSpinner();
        }
    };

    if (message) {
        return <p ref={messageRef}>{message}</p>;
    }

    return (
        <form class="flex flex-col gap-4" onSubmit={submit} noValidate>
            <p class="form-group">
                <label for="Email">{props.emailLabel}</label>
                <input
                    type="email"
                    id="Email"
                    class="email"
                    placeholder="navn@email.dk"
                    autocomplete="email"
                    value={email}
                    onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
                    onBlur={validate}
                />
                {validationKey === "required" && <span>{props.emailRequired}</span>}
                {validationKey === "format" && <span>{props.emailInvalid}</span>}
            </p>

            <button type="submit" class="button w-full">{props.submitButtonText}</button>
        </form>
    );
}

export const initEstatePropertyInterestFormIsland = () => createIsland("estate-property-interest", EstatePropertyInterestFormIsland, {
    modals: true,
});
